import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowIcon } from "../assets/ArrowIcon";
import { CheckIcon } from "../assets/CheckIcon";
import { ModalCloseButton } from "./ModalCloseButton";
import "./Dropdown.css";

export interface DropdownOption {
  label: string;
  value: string;
  disabled?: boolean;
}

export interface DropdownProps {
  label: string;
  options: DropdownOption[];
  value: string | null;
  onSelect: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  /**
   * Keep the label out of the visual layout but leave it for assistive tech. Use when a
   * dropdown sits in a dense row whose column header already names it: the label still
   * has to be distinct, because it is what `aria-label` is built from, and a row of
   * identically-named controls is unusable with a screen reader.
   */
  hideLabel?: boolean;
  /**
   * Show a filter box above the options. Worth enabling wherever the list grows with the
   * organization's data (teams, coaches, players) rather than being a fixed handful.
   */
  searchable?: boolean;
  /** Placeholder for the filter box. Defaults to "Search...". */
  searchPlaceholder?: string;
}

export function Dropdown({
  label,
  options,
  value,
  onSelect,
  placeholder = "Select...",
  error,
  disabled = false,
  hideLabel = false,
  searchable = false,
  searchPlaceholder = "Search...",
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Focus the filter on open so a coach can type straight away instead of clicking into it.
  useEffect(() => {
    if (isOpen && searchable) {
      searchInputRef.current?.focus();
    }
  }, [isOpen, searchable]);

  const visibleOptions = useMemo(() => {
    const trimmed = query.trim().toLowerCase();
    if (!searchable || !trimmed) return options;

    return options.filter((option) => {
      // Disabled entries are group headers. Keep them so a filtered list does not lose the
      // labels that give its groups meaning.
      if (option.disabled) return true;
      return option.label.toLowerCase().includes(trimmed);
    });
  }, [options, query, searchable]);

  const hasVisibleChoices = visibleOptions.some((option) => !option.disabled);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleSelect = (optionValue: string) => {
    onSelect(optionValue);
    setIsOpen(false);
    setQuery("");
  };

  const handleButtonClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOverlayClick = () => {
    setIsOpen(false);
    setQuery("");
  };

  return (
    <div className="dropdown">
      <label
        className={`dropdown__label${hideLabel ? " dropdown__label--hidden" : ""}`}
      >
        {label}
      </label>

      <button
        type="button"
        className={`dropdown__button ${
          error ? "dropdown__button--error" : ""
        } ${disabled ? "dropdown__button--disabled" : ""}`}
        onClick={handleButtonClick}
        disabled={disabled}
        aria-label={`Select ${label}`}
      >
        <span
          className={`dropdown__button-text ${
            !selectedOption ? "dropdown__button-text--placeholder" : ""
          }`}
        >
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ArrowIcon className="dropdown__chevron" />
      </button>

      {error && <span className="dropdown__error">{error}</span>}

      {isOpen && (
        <>
          <div className="dropdown__overlay" onClick={handleOverlayClick} />
          <div className="dropdown__modal">
            <div className="dropdown__modal-header">
              <h3 className="dropdown__modal-title">{label}</h3>
              <ModalCloseButton onClick={handleOverlayClick} />
            </div>
            {searchable && (
              <div className="dropdown__search">
                <input
                  ref={searchInputRef}
                  type="text"
                  className="dropdown__search-input"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label={`Search ${label}`}
                  data-testid="dropdown-search"
                />
              </div>
            )}
            <div className="dropdown__options">
              {searchable && !hasVisibleChoices && (
                <p className="dropdown__no-results">
                  No matches for &quot;{query.trim()}&quot;
                </p>
              )}
              {visibleOptions.map((option) => {
                const isSelected = value === option.value;
                const isDisabled = option.disabled === true;

                // Render disabled options as group headers
                if (isDisabled) {
                  return (
                    <div key={option.value} className="dropdown__group-header">
                      {option.label}
                    </div>
                  );
                }

                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`dropdown__option ${
                      isSelected ? "dropdown__option--selected" : ""
                    }`}
                    onClick={() => handleSelect(option.value)}
                  >
                    <span
                      className={`dropdown__option-text ${
                        isSelected ? "dropdown__option-text--selected" : ""
                      }`}
                    >
                      {option.label}
                    </span>
                    {isSelected && (
                      <CheckIcon className="dropdown__checkmark" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
